<template>
  <div class="extras-page">
    <h1>{{ $t('Extras') }}</h1>
    <p class="extras-intro">{{ $t('Elegí extras por pasajero y por tramo. Los precios son por vuelo.') }}</p>
    <p v-if="loadingCatalog">{{ $t('Cargando extras...') }}</p>

    <div v-for="pax in passengers" :key="pax.PassengerID" class="extras-pax">
      <h3>
        <font-awesome-icon icon="user"/>
        {{paxName(pax)}}
      </h3>

      <div v-for="segment in pax.Segments" :key="segment.SegmentID" class="extras-segment">
        <div class="extras-segment-title">
          <font-awesome-icon icon="plane"/>
          {{segment.DepartureStation}} → {{segment.ArrivalStation}}
          <span v-if="segment.DepartureDate"> · {{formatDate(segment.DepartureDate)}}</span>
        </div>

        <div class="extras-block" v-if="bagOptions.length">
          <h4>{{ $t('Equipaje de bodega') }}</h4>
          <div class="extra-card extra-card-wide" :class="{'is-selected': bagQty(segment) > 0, 'is-paid': lockedBagQty(segment) > 0}">
            <div class="extra-card-icon extra-card-icon-lg">
              <font-awesome-icon :icon="(bagUnitOption() && bagUnitOption().Icon) || 'suitcase'"/>
            </div>
            <div class="extra-card-body">
              <div class="extra-card-name">
                {{ bagUnitOption() ? extraTitle(bagUnitOption()) : $t('Valija') }}
                <span v-if="lockedBagQty(segment) > 0" class="extra-paid-badge">
                  {{ isFareIncluded(segment, 'baggage') ? $t('Incluido') : $t('Pagado') }}
                </span>
              </div>
              <div class="extra-card-dims" v-if="bagUnitOption() && (extraWeightLabel(bagUnitOption(), segment) || extraDimensionsLabel(bagUnitOption(), segment))">
                <span v-if="extraWeightLabel(bagUnitOption(), segment)">{{ extraWeightLabel(bagUnitOption(), segment) }}</span>
                <span v-if="extraWeightLabel(bagUnitOption(), segment) && extraDimensionsLabel(bagUnitOption(), segment)"> · </span>
                <span v-if="extraDimensionsLabel(bagUnitOption(), segment)">{{ extraDimensionsLabel(bagUnitOption(), segment) }}</span>
              </div>
              <div class="extra-card-sub" v-if="bagUnitOption() && extraRulesLabel(bagUnitOption(), segment)">
                {{ extraRulesLabel(bagUnitOption(), segment) }}
              </div>
              <div class="extra-bag-price-unit">{{ $t('Precio por valija:') }} <span>{{bagUnitPriceLabel(segment)}}</span></div>
              <div class="extra-card-price" v-if="bagQty(segment) > 0">
                {{ $t('Total') }} ({{bagQty(segment)}}): {{bagTotalPriceLabel(segment)}}
              </div>
            </div>
            <div class="extra-stepper">
              <button type="button" class="extra-stepper-btn"
                      :disabled="saving || bagQty(segment) <= lockedBagQty(segment) || isGroupLocked(segment, 'upgrade')"
                      @click="changeBags(pax, segment, -1)">−</button>
              <span class="extra-stepper-value">{{bagQty(segment)}}</span>
              <button type="button" class="extra-stepper-btn"
                      :disabled="saving || bagQty(segment) >= maxBags || isGroupLocked(segment, 'upgrade')"
                      @click="changeBags(pax, segment, 1)">+</button>
            </div>
          </div>
        </div>

        <div class="extras-block" v-if="bagQty(segment) > 0 && upgradeFor(segment)">
          <h4>{{ $t('Mejora de valija') }}</h4>
          <button type="button" class="extra-card"
                  :class="{'is-selected': !!selectedCode(segment, 'upgrade'), 'is-paid': isGroupLocked(segment, 'upgrade')}"
                  :disabled="saving || (isGroupLocked(segment, 'upgrade') && !!selectedCode(segment, 'upgrade'))"
                  @click="toggleUpgrade(pax, segment)">
            <div class="extra-card-icon">
              <font-awesome-icon icon="suitcase"/>
            </div>
            <div class="extra-card-body">
              <div class="extra-card-name">
                {{ upgradeFor(segment) ? extraTitle(upgradeFor(segment)) : $t('Agrandar valija') }}
                <span v-if="isGroupLocked(segment, 'upgrade')" class="extra-paid-badge">
                  {{ isFareIncluded(segment, 'upgrade') ? $t('Incluido') : $t('Pagado') }}
                </span>
              </div>
              <div class="extra-card-dims" v-if="upgradeFor(segment) && (extraWeightLabel(upgradeFor(segment), segment) || extraDimensionsLabel(upgradeFor(segment), segment))">
                <span v-if="extraWeightLabel(upgradeFor(segment), segment)">{{ extraWeightLabel(upgradeFor(segment), segment) }}</span>
                <span v-if="extraWeightLabel(upgradeFor(segment), segment) && extraDimensionsLabel(upgradeFor(segment), segment)"> · </span>
                <span v-if="extraDimensionsLabel(upgradeFor(segment), segment)">{{ extraDimensionsLabel(upgradeFor(segment), segment) }}</span>
              </div>
              <div class="extra-card-sub" v-if="upgradeFor(segment) && extraRulesLabel(upgradeFor(segment), segment)">
                {{ extraRulesLabel(upgradeFor(segment), segment) }}
              </div>
              <div class="extra-card-price">{{upgradePriceLabel(segment)}}</div>
            </div>
            <div class="extra-card-check" v-if="selectedCode(segment, 'upgrade')">
              <font-awesome-icon icon="check"/>
            </div>
          </button>
        </div>

        <div class="extras-block" v-if="showCabinBlock(segment)">
          <h4>{{ $t('Equipaje de cabina') }}</h4>
          <div class="extra-card-row">
            <div v-if="includedCabinExtra(segment)" class="extra-card is-selected is-paid">
              <div class="extra-card-icon">
                <font-awesome-icon :icon="includedCabinExtra(segment).Icon || 'briefcase'"/>
              </div>
              <div class="extra-card-body">
                <div class="extra-card-name">
                  {{ extraTitle(includedCabinExtra(segment)) }}
                  <span class="extra-paid-badge">{{ $t('Incluido') }}</span>
                </div>
                <div class="extra-card-dims" v-if="extraWeightLabel(includedCabinExtra(segment), segment) || extraDimensionsLabel(includedCabinExtra(segment), segment)">
                  <span v-if="extraWeightLabel(includedCabinExtra(segment), segment)">{{ extraWeightLabel(includedCabinExtra(segment), segment) }}</span>
                  <span v-if="extraWeightLabel(includedCabinExtra(segment), segment) && extraDimensionsLabel(includedCabinExtra(segment), segment)"> · </span>
                  <span v-if="extraDimensionsLabel(includedCabinExtra(segment), segment)">{{ extraDimensionsLabel(includedCabinExtra(segment), segment) }}</span>
                </div>
                <div class="extra-card-sub" v-if="extraRulesLabel(includedCabinExtra(segment), segment)">
                  {{ extraRulesLabel(includedCabinExtra(segment), segment) }}
                </div>
                <div class="extra-card-price">{{ $t('Sin cargo') }}</div>
              </div>
              <div class="extra-card-check">
                <font-awesome-icon icon="check"/>
              </div>
            </div>
            <button v-else type="button" class="extra-card"
                    :class="{'is-selected': !selectedCabinPurchase(segment)}"
                    :disabled="saving || isCabinPurchaseLocked(segment)"
                    @click="setGroup(pax, segment, 'cabin', null)">
              <div class="extra-card-icon">
                <font-awesome-icon icon="briefcase"/>
              </div>
              <div class="extra-card-body">
                <div class="extra-card-name">{{ $t('Sin carry on extra') }}</div>
                <div class="extra-card-price">{{ $t('Sin cargo') }}</div>
              </div>
            </button>
            <button type="button" class="extra-card" v-for="extra in cabinPurchaseOptionsFor(segment)" :key="extra.SSRCode"
                    :class="{
                      'is-selected': selectedCabinPurchase(segment) === extra.SSRCode,
                      'is-paid': selectedCabinPurchase(segment) === extra.SSRCode && isCabinPurchaseLocked(segment)
                    }"
                    :disabled="saving || (isCabinPurchaseLocked(segment) && selectedCabinPurchase(segment) !== extra.SSRCode)"
                    @click="setGroup(pax, segment, 'cabin', extra.SSRCode)">
              <div class="extra-card-icon">
                <font-awesome-icon :icon="extra.Icon || 'briefcase'"/>
              </div>
              <div class="extra-card-body">
                <div class="extra-card-name">
                  {{ extraTitle(extra) }}
                  <span v-if="selectedCabinPurchase(segment) === extra.SSRCode && isCabinPurchaseLocked(segment)" class="extra-paid-badge">
                    {{ $t('Pagado') }}
                  </span>
                </div>
                <div class="extra-card-dims" v-if="extraWeightLabel(extra, segment) || extraDimensionsLabel(extra, segment)">
                  <span v-if="extraWeightLabel(extra, segment)">{{ extraWeightLabel(extra, segment) }}</span>
                  <span v-if="extraWeightLabel(extra, segment) && extraDimensionsLabel(extra, segment)"> · </span>
                  <span v-if="extraDimensionsLabel(extra, segment)">{{ extraDimensionsLabel(extra, segment) }}</span>
                </div>
                <div class="extra-card-sub" v-if="extraRulesLabel(extra, segment)">{{ extraRulesLabel(extra, segment) }}</div>
                <div class="extra-card-price">{{formatPrice(extra, segment)}}</div>
              </div>
              <div class="extra-card-check" v-if="selectedCabinPurchase(segment) === extra.SSRCode">
                <font-awesome-icon icon="check"/>
              </div>
            </button>
          </div>
        </div>

        <div class="extras-block" v-if="checkinOptions.length">
          <h4>{{ $t('Check-in') }}</h4>
          <button type="button" class="extra-card" v-for="extra in checkinOptions" :key="extra.SSRCode"
                  :class="{
                    'is-selected': selectedCode(segment, 'checkin') === extra.SSRCode,
                    'is-paid': selectedCode(segment, 'checkin') === extra.SSRCode && isGroupLocked(segment, 'checkin')
                  }"
                  :disabled="saving || (isGroupLocked(segment, 'checkin') && selectedCode(segment, 'checkin') === extra.SSRCode)"
                  @click="toggleGroup(pax, segment, 'checkin', extra.SSRCode)">
            <div class="extra-card-icon">
              <font-awesome-icon icon="clipboard-check"/>
            </div>
            <div class="extra-card-body">
              <div class="extra-card-name">
                {{ $t(extra.Name) }}
                <span v-if="selectedCode(segment, 'checkin') === extra.SSRCode && isGroupLocked(segment, 'checkin')" class="extra-paid-badge">
                  {{ isFareIncluded(segment, 'checkin') ? $t('Incluido') : $t('Pagado') }}
                </span>
              </div>
              <div class="extra-card-sub" v-if="extra.Subtitle">{{ $t(extra.Subtitle) }}</div>
              <div class="extra-card-price">{{formatPrice(extra, segment)}}</div>
            </div>
            <div class="extra-card-check" v-if="selectedCode(segment, 'checkin') === extra.SSRCode">
              <font-awesome-icon icon="check"/>
            </div>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { getAvailableExtras, setBookingExtra } from '@/api/api';
  import { mapState } from 'vuex';
  import moment from 'moment';
  import { formatMoney } from '@/utils/utils';

  export default {
    name: 'extras-catalog',
    data: function () {
      return {
        extras: [],
        loadingCatalog: true,
        saving: false,
      }
    },
    computed: {
      ...mapState(['booking']),
      passengers: function () {
        if (!this.booking || !this.booking.Passengers) return [];
        return this.booking.Passengers.filter((pax) => !pax.Infant && pax.PaxType !== 'INF');
      },
      bagOptions: function () {
        return this.extras.filter((extra) => extra.Group === 'baggage').sort((a, b) => {
          return this.bagSlot(a.SSRCode) - this.bagSlot(b.SSRCode);
        });
      },
      maxBags: function () {
        if (!this.bagOptions.length) return 0;
        return Math.max.apply(null, this.bagOptions.map((extra) => this.bagSlot(extra.SSRCode) || 1));
      },
      cabinPurchaseOptions: function () {
        return this.extras.filter((extra) => {
          if (extra.Group !== 'cabin') return false;
          if (extra.InWebSales === false) return false;
          let amount = parseFloat(extra.BasePrice);
          return !isNaN(amount) && amount > 0;
        });
      },
      checkinOptions: function () {
        return this.extras.filter((extra) => extra.Group === 'checkin');
      }
    },
    mounted: function () {
      let bookingId = this.booking && this.booking.BookingID;
      getAvailableExtras((result) => {
        this.extras = Array.isArray(result) ? result : [];
        this.loadingCatalog = false;
      }, bookingId);
    },
    methods: {
      paxName: function (pax) {
        return [pax.FirstName, pax.MiddleName, pax.LastName].filter(Boolean).join(' ');
      },
      formatDate: function (d) {
        return moment(d).locale(this.$locale()).format('DD MMM YYYY');
      },
      priceForExtra: function (extra, segment) {
        if (!extra) return {amount: null, currency: 'ARS'};
        if (segment && extra.BySegment && extra.BySegment[String(segment.SegmentID)]) {
          let bySeg = extra.BySegment[String(segment.SegmentID)];
          return {amount: bySeg.Amount, currency: bySeg.CurrencyCode || extra.CurrencyCode || 'ARS'};
        }
        return {amount: extra.BasePrice, currency: extra.CurrencyCode || 'ARS'};
      },
      formatPrice: function (extra, segment) {
        let priced = this.priceForExtra(extra, segment);
        if (priced.amount === null || priced.amount === undefined || priced.amount === '') {
          return this.$t('Sin cargo');
        }
        let amount = parseFloat(priced.amount);
        if (isNaN(amount) || amount === 0) return this.$t('Sin cargo');
        return (priced.currency || 'ARS') + ' ' + formatMoney(amount);
      },
      catalogExtra: function (code) {
        if (!code) return null;
        return _.find(this.extras, (item) => item.SSRCode === code || item.FeeCode === code) || null;
      },
      policyForExtra: function (extra, segment) {
        if (!extra) return null;
        if (segment && extra.BySegment && extra.BySegment[String(segment.SegmentID)]) {
          return Object.assign({}, extra, extra.BySegment[String(segment.SegmentID)]);
        }
        return extra;
      },
      groupOf: function (code) {
        let extra = this.catalogExtra(code);
        return extra ? extra.Group : null;
      },
      formatCatalogNumber: function (value) {
        if (value === null || value === undefined || value === '') return value;
        let num = parseFloat(value);
        if (isNaN(num)) return value;
        return num === parseInt(num, 10) ? String(parseInt(num, 10)) : String(num);
      },
      extraTitle: function (extra) {
        if (!extra) return '';
        if (extra.I18nKey) {
          let keyed = this.$t(extra.I18nKey + '.title');
          if (keyed && keyed !== extra.I18nKey + '.title') return keyed;
        }
        return this.$t(extra.Name || extra.SSRCode);
      },
      extraWeightLabel: function (extra, segment) {
        let policy = this.policyForExtra(extra, segment);
        if (!policy) return null;
        if (policy.I18nKey && policy.MaxWeight != null) {
          let keyed = this.$t(policy.I18nKey + '.short_desc', {
            weight: this.formatCatalogNumber(policy.MaxWeight),
            unit: (policy.WeightUnit || 'KG').toLowerCase(),
          });
          if (keyed && keyed !== policy.I18nKey + '.short_desc') return keyed;
        }
        return null;
      },
      extraDimensionsLabel: function (extra, segment) {
        let policy = this.policyForExtra(extra, segment);
        if (!policy) return null;
        if (policy.I18nKey && policy.MaxDimLength != null && policy.MaxDimWidth != null && policy.MaxDimHeight != null) {
          let keyed = this.$t(policy.I18nKey + '.dimensions', {
            height: this.formatCatalogNumber(policy.MaxDimHeight),
            width: this.formatCatalogNumber(policy.MaxDimWidth),
            depth: this.formatCatalogNumber(policy.MaxDimLength),
            dim_unit: (policy.DimUnit || 'CM').toLowerCase(),
          });
          if (keyed && keyed !== policy.I18nKey + '.dimensions') return keyed;
        }
        return null;
      },
      extraRulesLabel: function (extra, segment) {
        let policy = this.policyForExtra(extra, segment);
        if (!policy) return null;
        if (policy.I18nKey) {
          let keyed = this.$t(policy.I18nKey + '.rules');
          if (keyed && keyed !== policy.I18nKey + '.rules') return keyed;
        }
        return null;
      },
      cabinCodesOnSegment: function (segment) {
        let codes = [];
        let seen = {};
        let push = (code) => {
          if (!code || this.groupOf(code) !== 'cabin' || seen[code]) return;
          seen[code] = true;
          codes.push(code);
        };
        if (segment && segment.Legs && segment.Legs[0] && segment.Legs[0].SSRs) {
          for (let ssr of segment.Legs[0].SSRs) push(ssr.SSRCode);
        }
        for (let fee of (segment && segment.Fees) || []) {
          push(fee.SSRCode || fee.FeeCode);
        }
        return codes;
      },
      isCabinCodeIncluded: function (segment, code) {
        if (!segment || !code) return false;
        for (let fee of segment.Fees || []) {
          let feeCode = fee.SSRCode || fee.FeeCode;
          if (feeCode === code && fee.Note === 'FARE_INCLUDED') return true;
        }
        if (segment.Legs && segment.Legs[0] && segment.Legs[0].SSRs) {
          for (let ssr of segment.Legs[0].SSRs) {
            if (ssr.SSRCode === code && ssr.IncludedInFare) return true;
          }
        }
        return false;
      },
      includedCabinExtra: function (segment) {
        let codes = this.cabinCodesOnSegment(segment);
        let included = _.find(codes, (code) => this.isCabinCodeIncluded(segment, code));
        if (!included) return null;
        let display = Object.assign({}, this.catalogExtra(included) || {SSRCode: included, Name: included, Group: 'cabin'});
        let name = null;
        for (let fee of (segment && segment.Fees) || []) {
          let code = fee.SSRCode || fee.FeeCode;
          if (code !== included) continue;
          name = fee.FeeDetail || (fee.SSR && fee.SSR.Name) || name;
        }
        if (segment.Legs && segment.Legs[0] && segment.Legs[0].SSRs) {
          for (let ssr of segment.Legs[0].SSRs) {
            if (ssr.SSRCode === included && ssr.SSR && ssr.SSR.Name) {
              name = name || ssr.SSR.Name;
            }
          }
        }
        if (name) display.Name = name;
        display.BasePrice = '0';
        return display;
      },
      cabinPurchaseOptionsFor: function (segment) {
        let included = {};
        for (let code of this.cabinCodesOnSegment(segment)) {
          if (this.isCabinCodeIncluded(segment, code)) included[code] = true;
        }
        return this.cabinPurchaseOptions.filter((extra) => !included[extra.SSRCode]);
      },
      selectedCabinPurchase: function (segment) {
        let codes = this.cabinCodesOnSegment(segment);
        return _.find(codes, (code) => !this.isCabinCodeIncluded(segment, code)) || null;
      },
      showCabinBlock: function (segment) {
        return this.cabinPurchaseOptionsFor(segment).length > 0 || !!this.includedCabinExtra(segment);
      },
      isCabinPurchaseLocked: function (segment) {
        let purchased = this.selectedCabinPurchase(segment);
        if (!purchased || !segment || !segment.Fees) return false;
        for (let fee of segment.Fees) {
          let code = fee.SSRCode || fee.FeeCode;
          if (code === purchased && fee.PaymentID) return true;
        }
        return false;
      },
      selectedCode: function (segment, group) {
        if (!segment) return null;
        if (group === 'cabin') {
          return this.selectedCabinPurchase(segment) || (this.includedCabinExtra(segment) || {}).SSRCode || null;
        }
        if (segment.Legs && segment.Legs[0] && segment.Legs[0].SSRs) {
          for (let ssr of segment.Legs[0].SSRs) {
            if (this.groupOf(ssr.SSRCode) === group) return ssr.SSRCode;
          }
        }
        if (segment.Fees) {
          for (let fee of segment.Fees) {
            let code = fee.SSRCode || fee.FeeCode;
            if (this.groupOf(code) === group) return code;
          }
        }
        return null;
      },
      isFareIncluded: function (segment, group) {
        if (!segment) return false;
        for (let fee of segment.Fees || []) {
          let code = fee.SSRCode || fee.FeeCode;
          if (this.groupOf(code) === group && fee.Note === 'FARE_INCLUDED') return true;
        }
        if (segment.Legs && segment.Legs[0] && segment.Legs[0].SSRs) {
          for (let ssr of segment.Legs[0].SSRs) {
            if (this.groupOf(ssr.SSRCode) === group && ssr.IncludedInFare) return true;
          }
        }
        return false;
      },
      isGroupPaid: function (segment, group) {
        if (!segment || !segment.Fees) return false;
        for (let fee of segment.Fees) {
          let code = fee.SSRCode || fee.FeeCode;
          if (this.groupOf(code) === group && fee.PaymentID) return true;
        }
        return false;
      },
      isGroupLocked: function (segment, group) {
        if (group === 'cabin') return this.isCabinPurchaseLocked(segment);
        return this.isGroupPaid(segment, group) || this.isFareIncluded(segment, group);
      },
      bagSlot: function (code) {
        if (!code) return 0;
        let match = /^(BAG|UB)(\d+)$/.exec(code);
        if (match) return parseInt(match[2], 10);
        return this.bagPiecesForCode(code) || 0;
      },
      bagPiecesForCode: function (code) {
        if (!code) return 0;
        let extra = _.find(this.bagOptions, (item) => item.SSRCode === code || item.FeeCode === code);
        if (extra && extra.Pieces) return extra.Pieces;
        let catalog = this.catalogExtra(code);
        return catalog && catalog.Pieces ? catalog.Pieces : 0;
      },
      isBaggageFeeLocked: function (fee) {
        if (!fee) return false;
        let code = fee.SSRCode || fee.FeeCode;
        if (this.groupOf(code) !== 'baggage') return false;
        if (fee.Note === 'FARE_INCLUDED') return true;
        return !!fee.PaymentID;
      },
      lockedBagQty: function (segment) {
        if (!segment) return 0;
        let total = 0;
        for (let fee of segment.Fees || []) {
          if (!this.isBaggageFeeLocked(fee)) continue;
          total += 1;
        }
        if (total) return total;
        if (segment.Legs && segment.Legs[0] && segment.Legs[0].SSRs) {
          for (let ssr of segment.Legs[0].SSRs) {
            if (this.groupOf(ssr.SSRCode) !== 'baggage') continue;
            if (!ssr.IncludedInFare) continue;
            total += 1;
          }
        }
        return total;
      },
      bagQty: function (segment) {
        if (!segment) return 0;
        let total = 0;
        let counted = {};
        for (let fee of segment.Fees || []) {
          let code = fee.SSRCode || fee.FeeCode;
          if (this.groupOf(code) !== 'baggage') continue;
          let key = code + ':' + (fee.SSRNumber || 1);
          counted[key] = true;
          total += 1;
        }
        if (segment.Legs && segment.Legs[0] && segment.Legs[0].SSRs) {
          for (let ssr of segment.Legs[0].SSRs) {
            if (this.groupOf(ssr.SSRCode) !== 'baggage') continue;
            let key = ssr.SSRCode + ':' + (ssr.SSRNumber || 1);
            if (counted[key]) continue;
            total += 1;
          }
        }
        return total;
      },
      bagOptionForQty: function (qty) {
        return _.find(this.bagOptions, (item) => this.bagSlot(item.SSRCode) === qty);
      },
      bagUnitOption: function () {
        return this.bagOptionForQty(1) || this.bagOptions[0] || null;
      },
      bagUnitPriceLabel: function (segment) {
        let nextSlot = this.lockedBagQty(segment) + 1;
        let nextBag = this.bagOptionForQty(nextSlot) || this.bagUnitOption();
        return this.formatPrice(nextBag, segment);
      },
      bagTotalPriceLabel: function (segment) {
        let qty = this.bagQty(segment);
        let locked = this.lockedBagQty(segment);
        if (qty <= locked) return this.$t('Sin cargo');
        let currency = null;
        let total = 0;
        for (let slot = locked + 1; slot <= qty; slot++) {
          let extra = this.bagOptionForQty(slot);
          let priced = this.priceForExtra(extra, segment);
          let amount = priced.amount != null ? parseFloat(priced.amount) : NaN;
          if (!isFinite(amount) || amount < 0) continue;
          total += amount;
          currency = priced.currency || currency || 'ARS';
        }
        if (!currency || total <= 0) return this.$t('Sin cargo');
        return currency + ' ' + formatMoney(total);
      },
      upgradeOptionsFor: function (segment) {
        let qty = this.bagQty(segment);
        if (!qty) return [];
        let upgrades = this.extras.filter((extra) => extra.Group === 'upgrade');
        let list = [];
        for (let slot = 1; slot <= qty; slot++) {
          let match = _.find(upgrades, (item) => this.bagSlot(item.SSRCode) === slot);
          if (match) list.push(match);
        }
        return list;
      },
      upgradeFor: function (segment) {
        return this.upgradeOptionsFor(segment)[0] || null;
      },
      upgradePriceLabel: function (segment) {
        let options = this.upgradeOptionsFor(segment);
        if (!options.length) return this.$t('Sin cargo');
        let currency = null;
        let total = 0;
        for (let extra of options) {
          let priced = this.priceForExtra(extra, segment);
          let amount = priced.amount != null ? parseFloat(priced.amount) : NaN;
          if (!isFinite(amount) || amount < 0) continue;
          total += amount;
          currency = priced.currency || currency || 'ARS';
        }
        if (!currency || total <= 0) return this.$t('Sin cargo');
        return currency + ' ' + formatMoney(total);
      },
      changeBags: function (pax, segment, delta) {
        if (this.isGroupLocked(segment, 'upgrade')) return;
        let locked = this.lockedBagQty(segment);
        let next = this.bagQty(segment) + delta;
        if (next < locked || next > this.maxBags) return;
        let hadUpgrade = !!this.selectedCode(segment, 'upgrade');
        let extra = next === 0 ? null : this.bagOptionForQty(next);
        this.setGroup(pax, segment, 'baggage', extra ? extra.SSRCode : null, () => {
          if (hadUpgrade && next > 0) {
            let freshPax = _.find(this.booking.Passengers, (item) => item.PassengerID === pax.PassengerID);
            let freshSeg = freshPax ? _.find(freshPax.Segments, (item) => item.SegmentID === segment.SegmentID) : null;
            let upgrade = freshSeg ? this.upgradeFor(freshSeg) : null;
            if (upgrade) this.setGroup(freshPax, freshSeg, 'upgrade', upgrade.SSRCode);
          }
        });
      },
      toggleUpgrade: function (pax, segment) {
        let extra = this.upgradeFor(segment);
        if (!extra) return;
        let selected = this.selectedCode(segment, 'upgrade');
        if (selected && this.isGroupLocked(segment, 'upgrade')) return;
        this.setGroup(pax, segment, 'upgrade', selected ? null : extra.SSRCode);
      },
      toggleGroup: function (pax, segment, group, ssrCode) {
        let selected = this.selectedCode(segment, group);
        if (selected === ssrCode && this.isGroupLocked(segment, group)) return;
        this.setGroup(pax, segment, group, selected === ssrCode ? null : ssrCode);
      },
      setGroup: function (pax, segment, group, ssrCode, after) {
        if (group === 'baggage') {
          let desired = ssrCode ? this.bagSlot(ssrCode) : 0;
          if (desired < this.lockedBagQty(segment)) return;
        } else if (this.isGroupLocked(segment, group) && this.selectedCode(segment, group) !== ssrCode) {
          return;
        }
        this.saving = true;
        this.$store.commit('loading', true);
        setBookingExtra({
          BookingID: this.booking.BookingID,
          PassengerID: pax.PassengerID,
          SegmentID: segment.SegmentID,
          Group: group,
          SSRCode: ssrCode,
        }, (result) => {
          this.$store.dispatch('updateBooking', result);
          this.saving = false;
          this.$store.commit('loading', false);
          if (after) after();
        }, (error) => {
          this.saving = false;
          this.$store.commit('loading', false);
          alert(error);
        });
      }
    }
  }
</script>
